import React, { useState } from 'react';
import { API_BASE as API } from '../../lib/apiBase';
import {
  X, Ticket, Wallet, CreditCard, Check, AlertCircle,
  Crown, Calendar, Clock, Loader2, Shield
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════════
   TICKET PURCHASE MODAL — Premium Payment Flow
   
   Payment options:
   1. Wallet balance (preferred)
   2. Stripe card payment
   
   Visual style: Dark with gold accents (VIP feeling)
═══════════════════════════════════════════════════════════════════════════════ */


const formatEuro = (n) => `${(n || 0).toFixed(2)}€`;

export const TicketPurchaseModal = ({ 
  show, 
  walletBalance = 0,
  onClose, 
  onSuccess,
  token,
  lang = 'es' 
}) => {
  const [paymentMethod, setPaymentMethod] = useState(walletBalance >= show.price ? 'wallet' : 'stripe');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const canUseWallet = walletBalance >= show.price;

  const handlePurchase = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API}/api/live/ticketed-show/${show.id}/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_method: paymentMethod,
          stripe_token: paymentMethod === 'stripe' ? 'mock_token' : null // TODO: Real Stripe
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Error al procesar el pago');
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.(data.ticket);
      }, 2000);

    } catch (e) {
      setError(e.message);
    }

    setLoading(false);
  };

  const scheduledDate = new Date(show.scheduled_at);

  return (
    <div 
      data-testid="ticket-purchase-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
    >
      <div 
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, #1a1505 0%, #0d0a02 100%)',
          border: '1px solid rgba(212,168,83,0.3)'
        }}
      >
        {/* Close button */}
        <button
          data-testid="close-purchase-modal"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
        >
          <X size={18} />
        </button>

        {/* Success State */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-green-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">
              {lang === 'es' ? '¡Ticket Adquirido!' : 'Ticket Purchased!'}
            </h3>
            <p className="text-white/60">
              {lang === 'es' 
                ? 'Tu acceso está confirmado. Te avisaremos cuando comience el show.'
                : 'Your access is confirmed. We\'ll notify you when the show starts.'}
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-6 border-b border-[#d4a853]/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#d4a853]/20 flex items-center justify-center">
                  <Ticket size={24} className="text-[#d4a853]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {lang === 'es' ? 'Adquirir Ticket' : 'Get Your Ticket'}
                  </h3>
                  <p className="text-sm text-[#d4a853]/80 flex items-center gap-1">
                    <Crown size={12} />
                    {lang === 'es' ? 'Acceso VIP exclusivo' : 'Exclusive VIP access'}
                  </p>
                </div>
              </div>

              {/* Show Info */}
              <div className="bg-black/30 rounded-xl p-4">
                <h4 className="font-semibold text-white mb-2">{show.title}</h4>
                <p className="text-sm text-white/50 mb-3">{show.artist_name}</p>
                <div className="flex items-center gap-4 text-xs text-white/40">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} className="text-[#d4a853]" />
                    {scheduledDate.toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} className="text-[#d4a853]" />
                    {scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="p-6">
              <p className="text-sm text-white/60 mb-4">
                {lang === 'es' ? 'Selecciona método de pago' : 'Select payment method'}
              </p>

              <div className="space-y-3">
                {/* Wallet Option */}
                <button
                  data-testid="payment-wallet"
                  onClick={() => canUseWallet && setPaymentMethod('wallet')}
                  disabled={!canUseWallet}
                  className={`w-full p-4 rounded-xl border transition-all ${
                    paymentMethod === 'wallet'
                      ? 'border-[#d4a853] bg-[#d4a853]/10'
                      : canUseWallet
                        ? 'border-white/10 hover:border-white/20'
                        : 'border-white/5 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        paymentMethod === 'wallet' ? 'bg-[#d4a853]/20' : 'bg-white/10'
                      }`}>
                        <Wallet size={20} className={paymentMethod === 'wallet' ? 'text-[#d4a853]' : 'text-white/60'} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-white">
                          {lang === 'es' ? 'Mi Monedero' : 'My Wallet'}
                        </p>
                        <p className="text-xs text-white/50">
                          {lang === 'es' ? 'Saldo:' : 'Balance:'} {formatEuro(walletBalance)}
                        </p>
                      </div>
                    </div>
                    {paymentMethod === 'wallet' && (
                      <div className="w-5 h-5 rounded-full bg-[#d4a853] flex items-center justify-center">
                        <Check size={12} className="text-black" />
                      </div>
                    )}
                  </div>
                  {!canUseWallet && (
                    <p className="text-xs text-red-400 mt-2 text-left">
                      {lang === 'es' ? 'Saldo insuficiente' : 'Insufficient balance'}
                    </p>
                  )}
                </button>

                {/* Stripe Option */}
                <button
                  data-testid="payment-stripe"
                  onClick={() => setPaymentMethod('stripe')}
                  className={`w-full p-4 rounded-xl border transition-all ${
                    paymentMethod === 'stripe'
                      ? 'border-[#d4a853] bg-[#d4a853]/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        paymentMethod === 'stripe' ? 'bg-[#d4a853]/20' : 'bg-white/10'
                      }`}>
                        <CreditCard size={20} className={paymentMethod === 'stripe' ? 'text-[#d4a853]' : 'text-white/60'} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-white">
                          {lang === 'es' ? 'Tarjeta de Crédito' : 'Credit Card'}
                        </p>
                        <p className="text-xs text-white/50">Visa, Mastercard, Amex</p>
                      </div>
                    </div>
                    {paymentMethod === 'stripe' && (
                      <div className="w-5 h-5 rounded-full bg-[#d4a853] flex items-center justify-center">
                        <Check size={12} className="text-black" />
                      </div>
                    )}
                  </div>
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center gap-2">
                  <AlertCircle size={16} className="text-red-400" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}

              {/* Price Summary */}
              <div className="mt-6 p-4 rounded-xl bg-black/30 border border-[#d4a853]/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/60">{lang === 'es' ? 'Precio del ticket' : 'Ticket price'}</span>
                  <span className="text-white font-medium">{formatEuro(show.price)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <span className="font-semibold text-white">Total</span>
                  <span className="text-2xl font-bold text-[#d4a853]">{formatEuro(show.price)}</span>
                </div>
              </div>

              {/* Purchase Button */}
              <button
                data-testid="confirm-purchase-btn"
                onClick={handlePurchase}
                disabled={loading}
                className="w-full mt-6 py-4 rounded-xl font-bold text-black transition-all duration-300 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #d4a853 0%, #b8942a 100%)',
                  boxShadow: '0 4px 15px rgba(212,168,83,0.4)'
                }}
              >
                {loading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    <Ticket size={18} />
                    {lang === 'es' ? 'Confirmar Compra' : 'Confirm Purchase'}
                  </>
                )}
              </button>

              {/* Security Note */}
              <p className="mt-4 text-center text-xs text-white/40 flex items-center justify-center gap-1">
                <Shield size={12} />
                {lang === 'es' 
                  ? 'Pago seguro y encriptado'
                  : 'Secure and encrypted payment'}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
